/**
 * Which page is on screen.
 *
 * A switch rather than a router: there is nothing to route *to* — the address
 * bar names a page once, on the way in, and after that the rail is the
 * navigation. What this file adds over a chain of `&&` in the shell is that
 * every page's props are gathered in one place, so a page that needs to know
 * which organization is open cannot be given the wrong one.
 */
import { AdminRequests } from './AdminRequests';
import { Affiliate } from './Affiliate';
import { Affiliates } from './AdminAffiliates';
import type React from 'react';
import { PageBody } from '../components/list';
import { Empty } from '../components/primitives';
import type { PageId } from '../components/nav';
import { BillingHistory, Dashboard, Devices, Downloads, LicencePlan, Profile } from './Member';
import { Organizations, Seats } from './Organizations';
import { AdminAccountDetail, AdminAccounts, AdminOrgDetail, AdminOrgs, AdminOverview } from './Admin';
import { AuditPage } from './cloud/Audit';
import { OrgMachinesPage } from './cloud/OrgMachines';
import { UsagePage } from './cloud/Usage';
import { RunsPage } from './cloud/Runs';
import { SpendPage } from './cloud/Spend';
import { StreamsPage } from './cloud/Streams';
import { WorkspaceHistory } from './cloud/WorkspaceHistory';
import { OrgSettingsPage } from './cloud/OrgSettings';
import { OrgWorkflowsPage } from './cloud/workflow/OrgWorkflows';
import { OrgSecretsPage } from './cloud/OrgSecrets';
import { RulesPage } from './cloud/rules/RulesPage';
import { OrgRulesPage } from './cloud/rules/rules-stats';
import { WorkspacePage, type WorkspaceSection } from './cloud/WorkspacePage';
import { Workspaces } from './cloud/Workspaces';
import { OrgOverviewPage } from './cloud/Overview';
import { FirstDay } from './cloud/FirstDay';
import { Team, Teams } from './cloud/Teams';
import { WorkspaceAccess } from './cloud/WorkspaceAccess';
import { SecurityPage } from './identity/Security';
import type { Me, Organization, WorkspaceRight } from '../lib/types';

/** The workspace row a page id names. */
const WORKSPACE_SECTION: Partial<Record<PageId, WorkspaceSection>> = {
  wsProjects: 'projects',
  wsIntegrations: 'integrations',
  wsSecrets: 'secrets',
  wsSettings: 'settings',
  wsAgents: 'agents'
};

export interface RouterProps {
  page: PageId;
  section: string | null;
  me: Me;
  /** The organization being looked at, `null` for a personal account. */
  organization: Organization | null;
  /** Whether this account administers that organization. */
  admin: boolean;
  /** The workspace entered, `null` outside one and for one being started. */
  workspaceId: string | null;
  /** What the caller may do in the workspace they entered, if they entered one. */
  workspaceRight: WorkspaceRight | null;
  /** Which team the team page is about. */
  teamId: string | null;
  /** Whether a workspace is being started rather than opened. */
  starting: boolean;
  accountId: string | null;
  focusVersion: number | null;
  onGo: (page: PageId, section?: string | null) => void;
  onEnterWorkspace: (workspaceId: string | null, right?: WorkspaceRight | null, name?: string | null) => void;
  onWorkspaceName: (name: string | null) => void;
  onOpenTeam: (teamId: string) => void;
  onTeamName: (name: string | null) => void;
  /**
   * Whether the member's overview is past its first day — `null` while the two
   * reads that decide it are still out. The header names the page and only
   * that page knows which of the two it is drawing.
   */
  onMemberSettled: (settled: boolean | null) => void;
  onInspectAccount: (id: string) => void;
  /** The open workspace's name, as the breadcrumb has it. */
  workspaceName: string | null;
  onFocusVersion: (version: number | null) => void;
  /** The settings were published — the rail's own reading of them is stale. */
  onSettingsPublished: () => void;
  onRefresh: () => Promise<void>;
  onSignedOut: () => void;
}

export function Router(props: RouterProps): React.JSX.Element {
  const { page, me, organization, admin, workspaceId, starting, onGo } = props;

  /* ── inside a workspace ───────────────────────────────────────────────── */

  const inWorkspace = WORKSPACE_SECTION[page];
  if (inWorkspace && organization && (workspaceId || starting)) {
    return (
      <WorkspacePage
        key={workspaceId ?? 'new'}
        me={me}
        orgId={organization.id}
        orgName={organization.name}
        workspaceId={workspaceId}
        section={inWorkspace}
        pane={props.section}
        onName={props.onWorkspaceName}
        onLeave={() => onGo('workspaces')}
        onCreated={(id) => props.onEnterWorkspace(id)}
        onBrowseRules={() => onGo('wsRules')}
      />
    );
  }

  // Access is its own page rather than a section of the editor: it is not part
  // of the document, and it is the one row of a workspace a member may be shown
  // without being able to change a thing.
  if (page === 'wsAccess' && organization && workspaceId) {
    return (
      <WorkspaceAccess
        organization={organization}
        workspaceId={workspaceId}
        workspaceName={props.workspaceName}
        mayShare={admin || props.workspaceRight === 'admin'}
      />
    );
  }

  // Rules are a page of their own too: read from GitHub through the
  // organization's App, never part of the document, and changed by pull request.
  if (page === 'wsRules' && organization && workspaceId) {
    return (
      <RulesPage
        key={workspaceId}
        orgId={organization.id}
        orgName={organization.name}
        workspaceId={workspaceId}
        onGo={() => onGo('wsProjects')}
      />
    );
  }

  if (page === 'wsHistory' && organization && workspaceId) {
    return (
      <PageBody>
        <WorkspaceHistory
          key={`${workspaceId}-${props.focusVersion ?? 'latest'}`}
          me={me}
          orgId={organization.id}
          workspaceId={workspaceId}
          focus={props.focusVersion}
        />
      </PageBody>
    );
  }

  /* ── the organization ─────────────────────────────────────────────────── */

  switch (page) {
    case 'workspaces':
      return organization ? (
        <Workspaces organization={organization} admin={admin} onOpen={props.onEnterWorkspace} />
      ) : (
        <PageBody>
          <Empty title="No organization">
            Workspaces belong to an organization, and this account holds no seat in one.
          </Empty>
        </PageBody>
      );

    case 'members':
      return (
        <PageBody>
          <Seats me={me} orgId={organization?.id ?? null} onRefresh={props.onRefresh} />
        </PageBody>
      );

    case 'orgMachines':
      return (
        <PageBody>
          <OrgMachinesPage organization={organization} />
        </PageBody>
      );

    case 'audit':
      return (
        <PageBody>
          <AuditPage
            me={me}
            orgId={organization?.id ?? null}
            onOrg={() => undefined}
            onOpenRevision={(_org, projectId, version) => {
              props.onFocusVersion(version);
              props.onEnterWorkspace(projectId);
              onGo('wsHistory');
            }}
          />
        </PageBody>
      );

    case 'usage':
      return (
        <PageBody>
          <UsagePage organization={organization} admin={admin} email={me.user.email} onGo={onGo} />
        </PageBody>
      );

    case 'runs':
      return (
        <PageBody>
          <RunsPage organization={organization} admin={admin} />
        </PageBody>
      );

    case 'spend':
      return (
        <PageBody>
          <SpendPage organization={organization} />
        </PageBody>
      );

    case 'streams':
      return (
        <PageBody>
          <StreamsPage organization={organization} />
        </PageBody>
      );

    case 'billing':
      return (
        <PageBody>
          <Organizations me={me} organization={organization} onRefresh={props.onRefresh} />
        </PageBody>
      );

    // The settings page draws its own body: its rows run to the page's edge,
    // and its publish verb sits above them rather than in the chrome.
    case 'orgSettings':
      return (
        <OrgSettingsPage
          organization={organization}
          section={props.section}
          admin={admin}
          onPublished={props.onSettingsPublished}
        />
      );

    case 'teams':
      return organization ? (
        <Teams organization={organization} onOpen={props.onOpenTeam} />
      ) : (
        <PageBody>
          <Empty title="No organization">Teams belong to an organization.</Empty>
        </PageBody>
      );

    case 'team':
      return organization && props.teamId ? (
        <Team
          organization={organization}
          teamId={props.teamId}
          onName={props.onTeamName}
          onLeave={() => onGo('teams')}
        />
      ) : (
        <PageBody>
          <Empty title="Pick a team">Choose one on Teams to open it.</Empty>
        </PageBody>
      );

    // The workflows draw their own body too: the canvas runs to the page's
    // edge, with Publish on the bar above it.
    case 'orgWorkflows':
      return <OrgWorkflowsPage organization={organization} admin={admin} />;

    case 'orgRules':
      return organization ? (
        <OrgRulesPage orgId={organization.id} />
      ) : (
        <PageBody>
          <Empty title="No organization">Rules belong to an organization.</Empty>
        </PageBody>
      );

    case 'orgSecrets':
      return <OrgSecretsPage organization={organization} admin={admin} />;

    case 'orgAgents':
      return (
        <OrgSettingsPage
          organization={organization}
          section={props.section}
          family="agents"
          admin={admin}
          onPublished={props.onSettingsPublished}
        />
      );

    case 'orgIntegrations':
      return (
        <OrgSettingsPage
          organization={organization}
          section={props.section}
          family="integrations"
          admin={admin}
          onPublished={props.onSettingsPublished}
        />
      );

    case 'orgSecurity':
      return (
        <PageBody>
          <SecurityPage organization={organization} section={props.section} />
        </PageBody>
      );

    /* ── what is mine ───────────────────────────────────────────────────── */

    // Three answers to one row, and they are three different questions. An
    // administrator's overview is about the organization — is somebody who
    // joins today handed something ready? A member's is about the day they
    // arrive: install, join, sign in. A personal account's is about itself —
    // its key, its machines, what it can download. None of the three is
    // another with rows removed, and the middle one used to be missing
    // entirely: a newcomer was shown the personal dashboard, which opened by
    // offering to sell them the licence their seat already carries.
    case 'overview':
      if (organization) {
        return admin ? (
          <OrgOverviewPage organization={organization} trialSeats={me.config.trial.seats} onGo={onGo} />
        ) : (
          <FirstDay organization={organization} onGo={onGo} onSettled={props.onMemberSettled} />
        );
      }
      return (
        <PageBody>
          <Dashboard me={me} onGo={onGo} onRefresh={props.onRefresh} />
        </PageBody>
      );

    case 'licence':
      return (
        <PageBody>
          <LicencePlan me={me} />
        </PageBody>
      );

    case 'invoices':
      return (
        <PageBody>
          <BillingHistory />
        </PageBody>
      );

    case 'machines':
      return (
        <PageBody>
          <Devices onGo={onGo} />
        </PageBody>
      );

    case 'downloads':
      return (
        <PageBody>
          <Downloads me={me} onGo={onGo} />
        </PageBody>
      );

    case 'profile':
      return (
        <PageBody>
          <Profile me={me} onRefresh={props.onRefresh} onSignedOut={props.onSignedOut} />
        </PageBody>
      );

    case 'affiliate':
      return (
        <PageBody>
          <Affiliate siteUrl={me.config.siteUrl} />
        </PageBody>
      );

    /* ── the deployment ─────────────────────────────────────────────────── */

    case 'adminAffiliates':
      return (
        <PageBody>
          <Affiliates />
        </PageBody>
      );

    case 'adminRequests':
      return (
        <PageBody>
          <AdminRequests />
        </PageBody>
      );

    case 'adminOverview':
      return (
        <PageBody>
          <AdminOverview onAffiliates={() => onGo('adminAffiliates')} />
        </PageBody>
      );

    case 'adminAccounts':
      return (
        <PageBody>
          <AdminAccounts onInspect={props.onInspectAccount} />
        </PageBody>
      );

    case 'adminAccountDetail':
      return (
        <PageBody>
          {props.accountId ? (
            <AdminAccountDetail id={props.accountId} onBack={() => onGo('adminAccounts')} />
          ) : (
            <Empty title="Pick an account">Choose one on Accounts to inspect it.</Empty>
          )}
        </PageBody>
      );

    case 'adminOrgs':
      return (
        <PageBody>
          <AdminOrgs onInspect={(id) => props.onInspectAccount(id)} />
        </PageBody>
      );

    case 'adminOrgDetail':
      return (
        <PageBody>
          {props.accountId ? (
            <AdminOrgDetail id={props.accountId} onBack={() => onGo('adminOrgs')} />
          ) : (
            <Empty title="Pick an organization">Choose one on Organizations to inspect it.</Empty>
          )}
        </PageBody>
      );

    default:
      return (
        <PageBody>
          <Empty title="Nothing here">That page is not part of this area.</Empty>
        </PageBody>
      );
  }
}
